const EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;
export const GA_CONSENT_STORAGE_KEY = 'squadverse_ga4_consent';
const GA_SCRIPT_ID = 'squadverse-ga4';
const GA_DEBUG_STORAGE_KEY = 'squadverse_ga4_debug';

/**
 * Stable GA4 identifiers for each game. These are analytics keys, never display
 * titles - renaming a game in the UI must not silently split its reporting.
 */
export const GA_GAMES = {
  THIRTY_EIGHT_O: '38_0',
  TRANSFER_WINDOW_CHALLENGE: 'transfer_window_challenge',
  WORLD_XI_CUP: 'world_xi_cup',
  CHAMPIONS_XI: 'champions_xi',
  CARD_CLASH: 'card_clash',
  SQUAD_BUILDER_CHALLENGE: 'squad_builder_challenge',
  BUDGET_SQUAD: 'budget_squad',
  BUY_KEEP_SELL: 'buy_keep_sell',
  START_BENCH_SELL: 'start_bench_sell',
  TIER_LIST: 'tier_list',
  IMPOSTER: 'imposter',
  ALPHABET: 'alphabet',
  TRANSFER_BUILDER: 'transfer_builder',
  SQUADVERSE_HUB: 'squadverse_hub',
};

/**
 * The complete GA4 product-event schema. Anything not listed here is dropped
 * before it can reach Google - this allowlist is the privacy boundary, so it
 * must stay small and must never gain a free-text or rating-derived field.
 */
const GA_EVENT_ALLOWLIST = {
  affiliate_click: ['provider', 'placement'],
  game_start: ['game', 'variant'],
  game_complete: ['game', 'variant', 'outcome', 'score', 'duration_seconds'],
  result_share: ['game', 'method'],
  replay_start: ['game', 'variant'],
  next_game_click: ['source_game', 'destination_game', 'placement'],
  analyst_profile_view: ['entry_point'],
  analyst_view_open: ['view', 'entry_point'],
  analyst_compare_ready: ['season_scope', 'entry_point'],
  analyst_radar_ready: ['axis_count'],
  analyst_share: ['artifact', 'method'],
};
const GA_AFFILIATE_PLACEMENTS = new Set([
  'homepage',
  'lineup_builder',
  'card_clash_setup',
  'thirty_eight_o_setup',
  'world_xi_cup_classic_setup',
  'thirty_eight_o_hub_footer',
  'world_xi_cup_hub_footer',
]);
const GA_SHARE_METHODS = new Set([
  'native_share',
  'copy_link',
  'copy_text',
  'copy_image',
  'download_image',
]);
const GA_ANALYST_ENTRY_POINTS = new Set([
  'direct',
  'search',
  'recent',
  'starter',
  'similar_player',
  'profile',
  'tab',
]);
const GA_ANALYST_VIEWS = new Set(['compare', 'scout', 'explore', 'custom_radar']);
const GA_ANALYST_SEASON_SCOPES = new Set(['same_season', 'cross_season']);
const GA_ANALYST_ARTIFACTS = new Set(['player_report', 'custom_radar']);
let runtimeAnalyticsConsent = null;

function getGaMeasurementId() {
  const value = process.env.REACT_APP_GA_MEASUREMENT_ID;
  return typeof value === 'string' ? value.trim() : '';
}

export function isGaConfigured() {
  return Boolean(getGaMeasurementId());
}

/**
 * Development-safe verification switch. Always on outside production builds,
 * and opt-in on the live site via `?ga_debug=1` (remembered for the tab only,
 * so it can survive SPA navigation while never affecting normal visitors).
 * When on, GA4 routes hits into DebugView and each send is logged locally.
 * It never changes which events fire or what they contain.
 */
export function isGaDebugEnabled() {
  if (process.env.NODE_ENV !== 'production') return true;
  if (typeof window === 'undefined') return false;
  try {
    if (/(?:^|[?&])ga_debug=1(?:&|$)/.test(window.location?.search || '')) {
      window.sessionStorage.setItem(GA_DEBUG_STORAGE_KEY, '1');
      return true;
    }
    return window.sessionStorage.getItem(GA_DEBUG_STORAGE_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function logGaDebug(name, properties) {
  if (!isGaDebugEnabled()) return;
  try {
    // eslint-disable-next-line no-console
    console.debug('[ga4]', name, properties);
  } catch (_) {
    // Logging must never interrupt a game action.
  }
}

export function getAnalyticsConsent() {
  if (runtimeAnalyticsConsent === 'granted' || runtimeAnalyticsConsent === 'denied') {
    return runtimeAnalyticsConsent;
  }
  try {
    if (typeof window === 'undefined') return 'unknown';
    const value = window.localStorage.getItem(GA_CONSENT_STORAGE_KEY);
    return value === 'granted' || value === 'denied' ? value : 'unknown';
  } catch (_) {
    return 'unknown';
  }
}

function setGaDisabled(disabled) {
  const measurementId = getGaMeasurementId();
  if (typeof window === 'undefined' || !measurementId) return;
  window[`ga-disable-${measurementId}`] = disabled;
}

function loadGaAfterConsent() {
  const measurementId = getGaMeasurementId();
  if (typeof window === 'undefined' || typeof document === 'undefined' || !measurementId || getAnalyticsConsent() !== 'granted') return false;

  setGaDisabled(false);
  const createdGtagStub = typeof window.gtag !== 'function';
  if (createdGtagStub) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
  }

  if (!document.getElementById(GA_SCRIPT_ID)) {
    window.gtag('js', new Date());
    // GA4 owner setting: turn off Enhanced Measurement > Page views for this
    // web stream. Squadverse sends pathname-only SPA page views below, so
    // automatic browser-history tracking would duplicate them and include the
    // current URL query string or hash.
    window.gtag('config', measurementId, {
      send_page_view: false,
      ...getSafeGaPageContext(),
      ...(isGaDebugEnabled() ? { debug_mode: true } : {}),
    });
    const script = document.createElement('script');
    script.id = GA_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.onerror = () => {
      setGaDisabled(true);
      script.remove();
      if (createdGtagStub) delete window.gtag;
    };
    document.head.appendChild(script);
  }
  return true;
}

export function setAnalyticsConsent(consent) {
  if (consent !== 'granted' && consent !== 'denied') return;

  // Keep the live choice even where a browser blocks localStorage. Persisting
  // it is useful, but must never prevent a reject/grant taking effect now.
  runtimeAnalyticsConsent = consent;

  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(GA_CONSENT_STORAGE_KEY, consent);
  } catch (_) {
    // The in-memory choice above remains active for this visit.
  }

  if (consent === 'granted') {
    initializeGaAfterConsent();
  } else {
    setGaDisabled(true);
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('consent', 'update', { analytics_storage: 'denied' });
      }
    } catch (_) {
      // A blocked analytics provider must not interrupt the privacy control.
    }
  }

  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('squadverse:analytics-consent', { detail: { consent } }));
    }
  } catch (_) {
    // A privacy preference must never prevent the app from being usable.
  }
}

export function initializeGaAfterConsent() {
  const loaded = loadGaAfterConsent();
  if (loaded && typeof window.gtag === 'function') {
    window.gtag('consent', 'update', { analytics_storage: 'granted' });
    try {
      window.dispatchEvent(new CustomEvent('squadverse:analytics-ready'));
    } catch (_) {
      // Analytics must never interrupt the app.
    }
  }
  return loaded;
}

function sanitizeGaPathname(pathname) {
  if (typeof pathname !== 'string') return null;
  const cleanPathname = pathname.split(/[?#]/, 1)[0] || '/';
  return cleanPathname.startsWith('/') ? cleanPathname.slice(0, 512) : null;
}

function anonymizeAnalystPathname(pathname) {
  const cleanPathname = sanitizeGaPathname(pathname) || '/';
  const segments = cleanPathname.split('/').filter(Boolean);
  if (segments[0] === 'analyst' && segments.length >= 4) {
    return `/${segments.slice(0, 3).join('/')}/player-profile`;
  }
  return cleanPathname;
}

function safeAnalyticsReferrer(referrer) {
  if (typeof referrer !== 'string' || !referrer.trim()) return null;
  try {
    const baseOrigin = typeof window !== 'undefined' && window.location?.origin && window.location.origin !== 'null'
      ? window.location.origin
      : 'http://localhost';
    const parsed = new URL(referrer, baseOrigin);
    if (parsed.origin !== baseOrigin) return parsed.origin.slice(0, 512);
    return `${baseOrigin}${anonymizeAnalystPathname(parsed.pathname)}`.slice(0, 2048);
  } catch (_) {
    return null;
  }
}

/**
 * GA automatically adds page_location, page_referrer and page_title to hits.
 * Keep those values safe even when a hit is sent from an Analyst player route,
 * where the browser URL and document title contain a player identity.
 */
export function getSafeGaPageContext(pathname, referrer) {
  const safePathname = anonymizeAnalystPathname(
    pathname || (typeof window !== 'undefined' ? window.location?.pathname : '/')
  );
  const origin = typeof window !== 'undefined' && window.location?.origin && window.location.origin !== 'null'
    ? window.location.origin
    : '';
  const context = {
    page_location: `${origin}${safePathname}`,
    page_title: safePathname.startsWith('/analyst') ? 'Squadverse Analyst' : 'Squadverse',
  };
  const safeReferrer = safeAnalyticsReferrer(
    referrer === undefined
      ? (typeof document !== 'undefined' ? document.referrer : '')
      : referrer
  );
  if (safeReferrer) context.page_referrer = safeReferrer;
  return context;
}

export function trackGaPageView(pathname) {
  if (typeof window === 'undefined' || getAnalyticsConsent() !== 'granted' || !isGaConfigured()) return false;
  const pagePath = anonymizeAnalystPathname(pathname);
  if (!pagePath || typeof window.gtag !== 'function') return false;

  try {
    const properties = {
      page_path: pagePath,
      ...getSafeGaPageContext(pagePath),
    };
    window.gtag('event', 'page_view', properties);
    logGaDebug('page_view', properties);
    return true;
  } catch (_) {
    return false;
  }
}

export function resetAnalyticsConsentForTests() {
  runtimeAnalyticsConsent = null;
}

function sanitizeGaProperties(name, properties) {
  const allowedKeys = GA_EVENT_ALLOWLIST[name];
  if (!allowedKeys || !properties || typeof properties !== 'object' || Array.isArray(properties)) return null;
  const sanitized = {};
  allowedKeys.forEach((key) => {
    const value = properties[key];
    if (typeof value === 'string' && /^[a-z0-9_]+$/.test(value)) sanitized[key] = value;
    if (typeof value === 'boolean') sanitized[key] = value;
    if (typeof value === 'number' && Number.isFinite(value)) sanitized[key] = Math.round(value);
  });
  if (
    name === 'affiliate_click'
    && (sanitized.provider !== 'surfshark' || !GA_AFFILIATE_PLACEMENTS.has(sanitized.placement))
  ) {
    return null;
  }
  return sanitized;
}

/**
 * Sends only the small GA4 product-event schema. Legacy calls must continue
 * to use trackEvent below, which remains Vercel-only.
 *
 * Prefer the named helpers below (trackGameStart, trackGameComplete,
 * trackResultShare, trackReplayStart, trackNextGameClick) over calling this
 * directly - they normalise identifiers and drop empty values so GA4 never
 * receives junk parameters.
 */
export function trackGaEvent(name, properties = {}) {
  if (typeof window === 'undefined' || getAnalyticsConsent() !== 'granted' || !isGaConfigured()) return false;
  const safeProperties = sanitizeGaProperties(name, properties);
  if (!safeProperties || typeof window.gtag !== 'function') return false;
  try {
    const payload = { ...safeProperties, ...getSafeGaPageContext() };
    window.gtag('event', name, payload);
    logGaDebug(name, payload);
    return true;
  } catch (_) {
    return false;
  }
}

/** Tracks only the approved affiliate partner and placement after GA consent. */
export function trackAffiliateClick(placement) {
  const safePlacement = normalizeGaEnum(placement, GA_AFFILIATE_PLACEMENTS);
  if (!safePlacement) return false;
  return trackGaEvent('affiliate_click', {
    provider: 'surfshark',
    placement: safePlacement,
  });
}

/**
 * Coerces a caller-supplied identifier into the stable snake_case form GA4
 * reports on. Returns '' for anything unusable so callers can bail out rather
 * than send a half-formed event.
 */
function normalizeGaId(value) {
  if (typeof value !== 'string') return '';
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
  return /^[a-z0-9_]+$/.test(normalized) ? normalized : '';
}

function normalizeGaEnum(value, allowedValues) {
  const normalized = normalizeGaId(value);
  return allowedValues.has(normalized) ? normalized : '';
}

function toGaCount(value) {
  return typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : undefined;
}

/** Fired when the player genuinely begins playing, not on a landing page view. */
export function trackGameStart({ game, variant } = {}) {
  const gameId = normalizeGaId(game);
  if (!gameId) return false;
  return trackGaEvent('game_start', {
    game: gameId,
    variant: normalizeGaId(variant) || undefined,
  });
}

/**
 * Fired exactly once per attempt, when a completed result is genuinely
 * reached. `outcome` and `score` must already be visible to the player -
 * never hidden ratings, strength bands or simulation weights.
 */
export function trackGameComplete({ game, variant, outcome, score, durationSeconds } = {}) {
  const gameId = normalizeGaId(game);
  if (!gameId) return false;
  return trackGaEvent('game_complete', {
    game: gameId,
    variant: normalizeGaId(variant) || undefined,
    outcome: normalizeGaId(outcome) || undefined,
    score: toGaCount(score),
    duration_seconds: toGaCount(durationSeconds),
  });
}

/** Fired only when a share or download action genuinely succeeds. */
export function trackResultShare({ game, method } = {}) {
  const gameId = normalizeGaId(game);
  const shareMethod = normalizeGaId(method);
  if (!gameId || !GA_SHARE_METHODS.has(shareMethod)) return false;
  return trackGaEvent('result_share', { game: gameId, method: shareMethod });
}

/** Fired when a genuinely new attempt begins after a previous one ended. */
export function trackReplayStart({ game, variant } = {}) {
  const gameId = normalizeGaId(game);
  if (!gameId) return false;
  return trackGaEvent('replay_start', {
    game: gameId,
    variant: normalizeGaId(variant) || undefined,
  });
}

/**
 * Fired when a cross-game recommendation is clicked. Same-game links (e.g.
 * 38-0 Classic offering 38-0 Packs) are deliberately dropped: this event
 * answers "does one game feed another", so counting mode switches inside a
 * single game would inflate it.
 */
export function trackNextGameClick({ sourceGame, destinationGame, placement } = {}) {
  const source = normalizeGaId(sourceGame);
  const destination = normalizeGaId(destinationGame);
  if (!source || !destination || source === destination) return false;
  return trackGaEvent('next_game_click', {
    source_game: source,
    destination_game: destination,
    placement: normalizeGaId(placement) || undefined,
  });
}

export function trackAnalystProfileView({ entryPoint } = {}) {
  const safeEntryPoint = normalizeGaEnum(entryPoint, GA_ANALYST_ENTRY_POINTS);
  if (!safeEntryPoint) return false;
  return trackGaEvent('analyst_profile_view', { entry_point: safeEntryPoint });
}

export function trackAnalystViewOpen({ view, entryPoint } = {}) {
  const safeView = normalizeGaEnum(view, GA_ANALYST_VIEWS);
  const safeEntryPoint = normalizeGaEnum(entryPoint, GA_ANALYST_ENTRY_POINTS);
  if (!safeView || !safeEntryPoint) return false;
  return trackGaEvent('analyst_view_open', { view: safeView, entry_point: safeEntryPoint });
}

export function trackAnalystCompareReady({ seasonScope, entryPoint } = {}) {
  const safeSeasonScope = normalizeGaEnum(seasonScope, GA_ANALYST_SEASON_SCOPES);
  const safeEntryPoint = normalizeGaEnum(entryPoint, GA_ANALYST_ENTRY_POINTS);
  if (!safeSeasonScope || !safeEntryPoint) return false;
  return trackGaEvent('analyst_compare_ready', { season_scope: safeSeasonScope, entry_point: safeEntryPoint });
}

export function trackAnalystRadarReady({ axisCount } = {}) {
  if (!Number.isInteger(axisCount) || axisCount < 3 || axisCount > 8) return false;
  return trackGaEvent('analyst_radar_ready', { axis_count: axisCount });
}

export function trackAnalystShare({ artifact, method } = {}) {
  const safeArtifact = normalizeGaEnum(artifact, GA_ANALYST_ARTIFACTS);
  const safeMethod = normalizeGaEnum(method, GA_SHARE_METHODS);
  if (!safeArtifact || !safeMethod) return false;
  return trackGaEvent('analyst_share', { artifact: safeArtifact, method: safeMethod });
}

/**
 * Local legacy event hook. Detailed product analytics uses the consent-gated
 * GA4 helpers above; these legacy events stay local so they do not consume
 * Vercel Hobby Web Analytics events.
 */
export function trackEvent(name, properties = {}) {
  if (!EVENT_NAME_PATTERN.test(name) || typeof window === 'undefined') return;

  const safeProperties = properties && typeof properties === 'object' ? properties : {};
  try {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[analytics]', name, safeProperties);
    }
    window.dispatchEvent(new CustomEvent('squadverse:analytics', {
      detail: { name, properties: safeProperties },
    }));
  } catch (_) {
    // Analytics must never interrupt a game action.
  }

}

export default trackEvent;
